import React, { useState } from "react";
import moment from "moment";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { MdRepeat } from "react-icons/md";
import {
  authApi,
  getAuthorizationHeader,
} from "../../../api-calls/user-requests";
import { LOCATION } from "../../../api-calls/api-urls";
import RepeatOptions from "./RepeatOptions";
import {
  describeRule,
  describeSettings,
  isLegacyRule,
  settingsFromRule,
  toRecurrencePayload,
  validateRepeatSettings,
} from "../../../utilities/recurrence";

const noteSx = {
  display: "flex",
  gap: 1,
  p: 1.5,
  borderRadius: "8px",
  fontSize: 13,
  lineHeight: 1.5,
  mb: 2,
};

/**
 * "Repeats" section on a task's detail page when the task is one day of a
 * repeating series (`location.series` from GET /locations/:slug). Leaders can
 * change the rule (PUT /locations/series/:id) or stop it
 * (PATCH /locations/series/:id/stop); days already assigned are never touched.
 */
const RepeatSeriesCard = ({ series, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [settings, setSettings] = useState(null);
  const [confirmStop, setConfirmStop] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const rule = series?.recurrence;
  if (!rule) return null;

  // The series' own start stays the anchor; the end can't be before today.
  const startDate = moment(rule.start_date, "YYYY-MM-DD").toDate();
  const today = moment().startOf("day").toDate();

  const openEditor = () => {
    setSettings(settingsFromRule(rule));
    setError("");
    setEditing(true);
  };

  const save = async () => {
    const problem = validateRepeatSettings(settings, startDate, today);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    try {
      await authApi.put(
        `${LOCATION}/series/${series._id}`,
        { recurrence: toRecurrencePayload(settings, startDate, today) },
        { headers: { Authorization: getAuthorizationHeader() } }
      );
      setEditing(false);
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update repeat");
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    try {
      await authApi.patch(
        `${LOCATION}/series/${series._id}/stop`,
        {},
        { headers: { Authorization: getAuthorizationHeader() } }
      );
      setConfirmStop(false);
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to stop repeat");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 4,
        p: 2,
        border: "1px solid #E5E7EB",
        borderRadius: "10px",
        backgroundColor: "#F9FAFC",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, minWidth: 0 }}>
        {/* Explicit colour: a global `svg { color: white }` rule leaks from the header CSS. */}
        <Box sx={{ mt: "2px", display: "flex" }}>
          <MdRepeat size={20} color={series.is_active ? "#256FE8" : "#9CA3AF"} />
        </Box>
        <div>
          <Typography sx={{ fontWeight: 600, fontSize: 15, color: "#0A1F41" }}>
            Repeating task
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#4B5563" }}>
            {describeRule(rule)}
          </Typography>
          {!series.is_active && (
            <Typography sx={{ fontSize: 13, color: "#B91C1C", mt: 0.5 }}>
              This repeat has stopped
            </Typography>
          )}
        </div>
      </Box>

      {series.is_active && (
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button variant="outlined" color="error" onClick={() => setConfirmStop(true)}>
            Stop repeating
          </Button>
          <Button variant="contained" onClick={openEditor}>
            Edit repeat
          </Button>
        </Box>
      )}

      {/* Edit */}
      <Dialog open={editing} onClose={() => !busy && setEditing(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          Edit repeat
          <IconButton onClick={() => setEditing(false)} size="small" disabled={busy}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {settings && (
            <>
              <Typography sx={{ fontSize: 16, fontWeight: 500, mb: 2 }}>
                {describeSettings(settings, startDate, today) || "—"}
              </Typography>
              <Box sx={{ ...noteSx, backgroundColor: "#EFF6FF", color: "#1E40AF" }}>
                Changes apply to visits that haven't been assigned yet. Visits already
                assigned stay as they are.
              </Box>
              {isLegacyRule(rule) && (
                <Box sx={{ ...noteSx, backgroundColor: "#FFFBEB", color: "#92400E" }}>
                  Currently: {describeRule(rule)}. This pattern is no longer offered —
                  saving replaces it with the one below.
                </Box>
              )}
              <RepeatOptions
                value={settings}
                onChange={(next) => {
                  setSettings(next);
                  setError("");
                }}
                startDate={startDate}
                floorDate={today}
                stacked
              />
              {error && (
                <Typography sx={{ color: "#B91C1C", fontSize: 14 }}>{error}</Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={busy}>
            {busy ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stop */}
      <Dialog open={confirmStop} onClose={() => !busy && setConfirmStop(false)} maxWidth="xs">
        <DialogTitle>Stop repeating?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: "#4B5563" }}>
            Future visits won't be assigned any more. Visits already assigned stay as
            they are.
          </Typography>
          {error && (
            <Typography sx={{ color: "#B91C1C", fontSize: 14, mt: 1 }}>{error}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setConfirmStop(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={stop} disabled={busy}>
            {busy ? <CircularProgress size={20} color="inherit" /> : "Stop repeating"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RepeatSeriesCard;
